import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
    Container, Typography, Box, Paper, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, TextField,
    Menu, MenuItem, InputAdornment, Chip, Stack,
    ToggleButton, ToggleButtonGroup, Autocomplete, FormControlLabel, Checkbox,
    useMediaQuery, useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RepeatIcon from '@mui/icons-material/Repeat';
import { RecurringInterval, Currency, TransactionType, PaymentMethod } from '../constants/enums';
import LoadingOverlay from '../components/LoadingOverlay';
import FormDialog, { FormField, FormSection, arborGreen, filledInputSx } from '../components/FormDialog';
import { getTodayInTimezone } from '../utils/date';
import { useTranslation } from 'react-i18next';
import { filterAndSortCategories, getCategoryOptionLabel } from '../utils/category';
import { handleNumericFocus } from '../utils/inputUtils';

export default function RecurringTransactions() {
    const { t } = useTranslation();
    const { id: ledgerId } = useParams();
    const { api, preferredCurrency, preferredTimezone, formatCurrency } = useAuth();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Data State
    const [transactions, setTransactions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [creditCards, setCreditCards] = useState([]);
    const [userRole, setUserRole] = useState('viewer');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Dialog State
    const [open, setOpen] = useState(false);
    const [currentTx, setCurrentTx] = useState(null); // null = create mode
    const [formData, setFormData] = useState({
        description: '', amount: '', currency: preferredCurrency, type: TransactionType.DEBIT,
        category_id: '', interval: RecurringInterval.MONTHLY, interval_config: null,
        start_date: '', end_date: '', is_active: true,
        payment_method: PaymentMethod.CASH, credit_card_id: ''
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLinkedEntries, setDeleteLinkedEntries] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTxId, setSelectedTxId] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const [txRes, catRes, ledgerRes, ccRes] = await Promise.all([
                api.get(`/ledger/ledgers/${ledgerId}/recurring-transactions/`),
                api.get(`/categories/ledger/${ledgerId}`),
                api.get('/ledger/ledgers'),
                api.get(`/credit-cards/ledger/${ledgerId}`)
            ]);
            const sortedTx = [...(txRes.data || [])].sort((a, b) => (a.description || '').localeCompare(b.description || ''));
            setTransactions(sortedTx);
            setCategories(catRes.data);
            setCreditCards(ccRes.data);
            const currentLedger = ledgerRes.data.find(l => l.id?.toString() === ledgerId?.toString());
            if (currentLedger) {
                setUserRole(currentLedger.role || 'viewer');
            }
        } catch (err) {
            console.error("Failed to fetch data", err);
            setError('Failed to load recurring transactions.');
        } finally {
            setIsLoading(false);
        }
    }, [api, ledgerId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatCurrencyInput = (value) => {
        if (!value) return '';
        let val = value.toString().replace(/[^0-9.]/g, '');
        const parts = val.split('.');
        let cleanVal = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : val;

        if (!cleanVal) return '';

        let [intPart, decPart] = cleanVal.split('.');
        if (intPart === '') intPart = '0';

        if (intPart.length > 1 && intPart.startsWith('0')) {
            intPart = parseInt(intPart, 10).toString();
        }

        const formattedInt = new Intl.NumberFormat('en-US').format(intPart);

        if (decPart !== undefined) {
            decPart = decPart.slice(0, 2);
            return `${formattedInt}.${decPart}`;
        }
        return formattedInt;
    };

    const resetForm = useCallback(() => {
        setCurrentTx(null);
        setFormData({
            description: '',
            amount: '',
            currency: preferredCurrency,
            type: TransactionType.DEBIT,
            category_id: '',
            interval: RecurringInterval.MONTHLY,
            interval_config: null,
            start_date: getTodayInTimezone(preferredTimezone),
            end_date: '',
            is_active: true,
            payment_method: PaymentMethod.CASH,
            credit_card_id: ''
        });
    }, [preferredCurrency, preferredTimezone]);

    const handleOpenAdd = () => {
        resetForm();
        setOpen(true);
    };

    const handleOpenEdit = (tx) => {
        setCurrentTx(tx);
        setFormData({
            description: tx.description,
            amount: tx.amount ? Math.abs(Number(tx.amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
            currency: tx.currency || preferredCurrency,
            type: tx.amount && Number(tx.amount) < 0 ? TransactionType.CREDIT : TransactionType.DEBIT,
            category_id: tx.category_id || '',
            interval: tx.interval,
            interval_config: tx.interval_config || null,
            start_date: tx.start_date.split('T')[0],
            end_date: tx.end_date ? tx.end_date.split('T')[0] : '',
            is_active: tx.is_active,
            payment_method: tx.credit_card_id ? PaymentMethod.CREDIT_CARD : PaymentMethod.CASH,
            credit_card_id: tx.credit_card_id || ''
        });
        setOpen(true);
        handleCloseMenu();
    };

    const handleSave = async () => {
        try {
            // Validation
            if (formData.interval === RecurringInterval.DAYS_OF_WEEK && (!formData.interval_config?.days_of_week || formData.interval_config.days_of_week.length === 0)) {
                showToast("Please select at least one day of the week.", "error");
                return;
            }
            if (formData.interval === RecurringInterval.DAYS_OF_MONTH && (!formData.interval_config?.days_of_month || formData.interval_config.days_of_month.length === 0)) {
                showToast("Please select at least one day of the month.", "error");
                return;
            }

            const parsedAmount = parseFloat((formData.amount || '').toString().replace(/,/g, ''));
            const finalAmount = formData.type === TransactionType.CREDIT ? -Math.abs(parsedAmount) : Math.abs(parsedAmount);

            const payload = {
                description: formData.description,
                amount: finalAmount,
                currency: formData.currency,
                category_id: formData.category_id || null,
                interval: formData.interval,
                interval_config: formData.interval_config,
                start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
                end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
                is_active: formData.is_active,
                credit_card_id: formData.type === TransactionType.DEBIT && formData.payment_method === PaymentMethod.CREDIT_CARD ? formData.credit_card_id : null
            };

            setIsSaving(true);
            if (currentTx) {
                await api.put(`/ledger/ledgers/${ledgerId}/recurring-transactions/${currentTx.id}`, payload);
            } else {
                await api.post(`/ledger/ledgers/${ledgerId}/recurring-transactions/`, payload);
            }
            setOpen(false);
            fetchData();
        } catch (err) {
            console.error("Failed to save transaction", err);
            showToast("Failed to save recurring transaction. Ensure amount is not zero and all fields are correct.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const initiateDelete = () => {
        setDeleteLinkedEntries(false);
        setDeleteDialogOpen(true);
        setAnchorEl(null);
    };

    const confirmDelete = async () => {
        if (!selectedTxId) return;
        setIsDeleting(true);
        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/recurring-transactions/${selectedTxId}?delete_linked_entries=${deleteLinkedEntries}`);
            setDeleteDialogOpen(false);
            setSelectedTxId(null);
            fetchData();
        } catch (err) {
            console.error("Failed to delete recurring transaction", err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMenuClick = (event, tx) => {
        setAnchorEl(event.currentTarget);
        setSelectedTxId(tx.id);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedTxId(null);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {t('Recurring')}
                    </Typography>
                </Box>
                {userRole !== 'viewer' && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            py: { xs: 1.2, sm: 1 }
                        }}
                    >
                        {t('Add Scheduled Transaction')}
                    </Button>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <LoadingOverlay open={isLoading} />

            {isMobile ? (
                <Stack spacing={2}>
                    {!isLoading && transactions.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
                            <RepeatIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                            <Typography color="text.secondary">{t('No data available')}</Typography>
                        </Paper>
                    ) : (
                        transactions.map((row) => (
                            <Paper key={row.id} elevation={0} sx={{ p: 2, opacity: row.is_active ? 1 : 0.6 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Chip
                                        size="small"
                                        label={row.is_active ? t('Active') : t('Inactive')}
                                        color={row.is_active ? "success" : "default"}
                                        variant={row.is_active ? "filled" : "outlined"}
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                    <Box>
                                        <IconButton size="small" onClick={(e) => handleMenuClick(e, row)} sx={{ p: 0.5 }} aria-label="More actions">
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{row.description}</Typography>
                                <Typography variant="h6" color={Number(row.amount) < 0 ? "success.main" : "error.main"} sx={{ fontWeight: 700, mb: 1 }}>
                                    {formatCurrency(row.amount, row.currency)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                                    {(row.interval || '').replace(/_/g, ' ')} • Next: {new Date(row.next_run_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </Typography>
                                {row.category && (
                                    <Chip
                                        size="small"
                                        label={row.category.name}
                                        sx={{
                                            mt: 1,
                                            bgcolor: row.category.color ? `${row.category.color}15` : 'transparent',
                                            color: row.category.color || 'primary.main',
                                            borderColor: row.category.color || 'primary.main',
                                            border: 1,
                                            fontSize: '0.75rem',
                                            height: 24
                                        }}
                                    />
                                )}
                            </Paper>
                        ))
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: '100%', '& .MuiTableCell-root': { px: { xs: 1, sm: 2 } } }}>
                        <TableHead sx={{ backgroundColor: 'action.hover' }}>
                            <TableRow>
                                <TableCell>{t('Status')}</TableCell>
                                <TableCell>{t('Description')}</TableCell>
                                <TableCell>{t('Category')}</TableCell>
                                <TableCell>{t('Interval')}</TableCell>
                                <TableCell>{t('Next Due Date')}</TableCell>
                                <TableCell align="right">{t('Amount')}</TableCell>
                                {userRole !== 'viewer' && <TableCell align="right">{t('Actions')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!isLoading && transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                        <RepeatIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                                        <Typography color="text.secondary">No recurring rules found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transactions.map((row) => (
                                    <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, opacity: row.is_active ? 1 : 0.6 }}>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.is_active ? "Active" : "Inactive"}
                                                color={row.is_active ? "success" : "default"}
                                                variant={row.is_active ? "filled" : "outlined"}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>{row.description}</TableCell>
                                        <TableCell>
                                            {row.category ? (
                                                <Chip
                                                    size="small"
                                                    label={row.category.name}
                                                    sx={{
                                                        bgcolor: row.category.color ? `${row.category.color}20` : 'transparent',
                                                        color: row.category.color || 'primary.main',
                                                        borderColor: row.category.color || 'primary.main',
                                                        border: 1
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    Uncategorized
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>
                                            {(row.interval || '').replace(/_/g, ' ')}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {new Date(row.next_run_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: Number(row.amount) < 0 ? 'success.main' : 'error.main' }}>
                                                {formatCurrency(row.amount, row.currency)}
                                            </Typography>
                                        </TableCell>
                                        {userRole !== 'viewer' && (
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={(e) => handleMenuClick(e, row)} sx={{ color: 'text.secondary' }} aria-label="More actions">
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}            {/* Create/Edit Form Dialog */}
            <FormDialog
                open={open}
                onClose={isSaving ? undefined : () => { setOpen(false); resetForm(); }}
                onSubmit={handleSave}
                title={currentTx ? t('Edit Recurring Rule') : t('New Recurring Rule')}
                subtitle={currentTx 
                    ? t('Modify the schedule and details of your recurring transaction.') 
                    : t('Create a schedule to automate your recurring income or expenses.')
                }
                saveLabel={currentTx ? t('Update Rule') : t('Create Rule')}
                saving={isSaving}
                maxWidth="sm"
            >
                <Stack spacing={3}>
                    <FormField label={t("Description")} id="description" required>
                        <TextField
                            id="description"
                            autoFocus
                            placeholder={t("e.g. Monthly Rent")}
                            fullWidth
                            variant="filled"
                            InputProps={{ disableUnderline: true }}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            sx={filledInputSx}
                        />
                    </FormField>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        gap: 3,
                        alignItems: { xs: 'stretch', sm: 'flex-start' }
                    }}>
                        <FormField label={t("Amount")} id="amount" required sx={{ flex: 1, width: '100%' }}>
                            <TextField
                                id="amount"
                                type="text"
                                fullWidth
                                variant="filled"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start" sx={{ color: arborGreen, fontWeight: 700, height: '100%', mt: 0 }}>$</InputAdornment>,
                                    disableUnderline: true 
                                }}
                                slotProps={{
                                    htmlInput: { inputMode: 'decimal' }
                                }}
                                value={formData.amount}
                                onFocus={(e) => handleNumericFocus(e)}
                                onChange={(e) => setFormData({ ...formData, amount: formatCurrencyInput(e.target.value) })}
                                sx={filledInputSx}
                            />
                        </FormField>

                        <FormField label={t("Transaction Type")} id="transaction_type" required sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <ToggleButtonGroup
                                id="transaction_type"
                                value={formData.type}
                                exclusive
                                onChange={(e, newType) => {
                                    if (newType !== null) {
                                        setFormData({ ...formData, type: newType, category_id: '' });
                                    }
                                }}
                                sx={{
                                    width: { xs: '100%', sm: 'auto' },
                                    display: 'flex',
                                    '& .MuiToggleButton-root': {
                                        flex: { xs: 1, sm: 'initial' },
                                        px: { xs: 1.5, sm: 2 }
                                    }
                                }}
                            >
                                <ToggleButton value={TransactionType.DEBIT} color="error">{t("Expense")}</ToggleButton>
                                <ToggleButton value={TransactionType.CREDIT} color="success">{t("Income")}</ToggleButton>
                            </ToggleButtonGroup>
                        </FormField>
                    </Box>

                    <FormField label={t("Category")} id="category_id">
                        <Autocomplete
                            id="category_id"
                            options={filterAndSortCategories(categories, null, t)}
                            getOptionLabel={(option) => getCategoryOptionLabel(option, categories, t)}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            value={categories.find(c => c.id === formData.category_id) || null}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, category_id: newValue ? newValue.id : '' });
                            }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    placeholder={t("Select a category")} 
                                    fullWidth 
                                    variant="filled"
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    sx={filledInputSx}
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...restProps } = props;
                                const paddingLeft = option.parent_id ? '24px' : '0px';
                                return (
                                    <li key={key || option.id} {...restProps}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: paddingLeft }}>
                                            {option.color && (
                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.color }} />
                                            )}
                                            {t(option.name)}
                                        </Box>
                                    </li>
                                );
                            }}
                        />
                    </FormField>

                    {formData.type === TransactionType.DEBIT && (
                        <FormSection title={t("Payment Details")}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                <FormField label={t("Payment Method")} id="payment_method" sx={{ flex: 1 }}>
                                    <TextField
                                        id="payment_method"
                                        select
                                        variant="filled"
                                        fullWidth
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            payment_method: e.target.value,
                                            credit_card_id: e.target.value === PaymentMethod.CASH ? '' : formData.credit_card_id
                                        })}
                                        sx={filledInputSx}
                                        SelectProps={{ disableUnderline: true }}
                                        inputProps={{ 'aria-label': t('Payment Method') }}
                                    >
                                        <MenuItem value={PaymentMethod.CASH}>{t("Cash / Bank Account")}</MenuItem>
                                        <MenuItem value={PaymentMethod.CREDIT_CARD}>{t("Credit Card")}</MenuItem>
                                    </TextField>
                                </FormField>

                                {formData.payment_method === PaymentMethod.CREDIT_CARD && (
                                    <FormField label={t("Credit Card")} id="credit_card_id" required sx={{ flex: 1 }}>
                                        <Autocomplete
                                            id="credit_card_id"
                                            options={creditCards}
                                            getOptionLabel={(option) => `${option.name} (*${option.last_4_digits})`}
                                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                                            value={creditCards.find(cc => cc.id === formData.credit_card_id) || null}
                                            onChange={(event, newValue) => {
                                                setFormData({ ...formData, credit_card_id: newValue ? newValue.id : '' });
                                            }}
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    placeholder={t("Select card")} 
                                                    fullWidth 
                                                    variant="filled"
                                                    error={!formData.credit_card_id}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                    sx={filledInputSx}
                                                />
                                            )}
                                        />
                                    </FormField>
                                )}
                            </Stack>
                        </FormSection>
                    )}

                    <FormSection title={t("Schedule Configuration")}>
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                                <FormField label={t("Interval")} id="interval" required sx={{ flex: 1 }}>
                                    <TextField
                                        id="interval"
                                        select
                                        variant="filled"
                                        fullWidth
                                        value={formData.interval}
                                        onChange={(e) => {
                                            const newInterval = e.target.value;
                                            let newConfig = null;
                                            if (newInterval === RecurringInterval.DAYS_OF_WEEK) {
                                                newConfig = { days_of_week: [] };
                                            } else if (newInterval === RecurringInterval.DAYS_OF_MONTH) {
                                                newConfig = { days_of_month: [] };
                                            }
                                            setFormData({ ...formData, interval: newInterval, interval_config: newConfig });
                                        }}
                                        sx={filledInputSx}
                                        SelectProps={{ disableUnderline: true }}
                                        inputProps={{ 'aria-label': t('Interval') }}
                                    >
                                        <MenuItem value={RecurringInterval.DAILY}>{t("Daily")}</MenuItem>
                                        <MenuItem value={RecurringInterval.WEEKLY}>{t("Weekly")}</MenuItem>
                                        <MenuItem value={RecurringInterval.BI_WEEKLY}>{t("Bi-weekly")}</MenuItem>
                                        <MenuItem value={RecurringInterval.SEMI_MONTHLY}>{t("Semi-monthly")}</MenuItem>
                                        <MenuItem value={RecurringInterval.MONTHLY}>{t("Monthly")}</MenuItem>
                                        <MenuItem value={RecurringInterval.QUARTERLY}>{t("Quarterly")}</MenuItem>
                                        <MenuItem value={RecurringInterval.SEMI_ANNUALLY}>{t("Semi-annually")}</MenuItem>
                                        <MenuItem value={RecurringInterval.YEARLY}>{t("Yearly")}</MenuItem>
                                        <MenuItem value={RecurringInterval.DAYS_OF_WEEK}>{t("Day(s) of Week")}</MenuItem>
                                        <MenuItem value={RecurringInterval.DAYS_OF_MONTH}>{t("Day(s) of Month")}</MenuItem>
                                    </TextField>
                                </FormField>

                                <FormField label={t("Currency")} id="currency" required sx={{ flex: 1 }}>
                                    <Autocomplete
                                        id="currency"
                                        options={[Currency.CAD, Currency.USD, Currency.CNY]}
                                        value={formData.currency || Currency.CAD}
                                        onChange={(e, newValue) => {
                                            if (newValue) {
                                                setFormData(prev => ({ ...prev, currency: newValue }));
                                            }
                                        }}
                                        disableClearable
                                        autoHighlight
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                fullWidth
                                                sx={filledInputSx}
                                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                inputProps={{ ...params.inputProps, 'aria-label': t('Currency') }}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Box>

                            {formData.interval === RecurringInterval.DAYS_OF_WEEK && (
                                <FormField label={t("Select Days of Week")}>
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pt: 0.5 }}>
                                        {[
                                            { label: t('Mon'), val: 0 },
                                            { label: t('Tue'), val: 1 },
                                            { label: t('Wed'), val: 2 },
                                            { label: t('Thu'), val: 3 },
                                            { label: t('Fri'), val: 4 },
                                            { label: t('Sat'), val: 5 },
                                            { label: t('Sun'), val: 6 },
                                        ].map((dayObj) => {
                                            const isSelected = formData.interval_config?.days_of_week?.includes(dayObj.val);
                                            return (
                                                <Chip
                                                    key={dayObj.val}
                                                    label={dayObj.label}
                                                    clickable
                                                    onClick={() => {
                                                        const currentDays = formData.interval_config?.days_of_week || [];
                                                        const newDays = isSelected
                                                            ? currentDays.filter(d => d !== dayObj.val)
                                                            : [...currentDays, dayObj.val].sort();
                                                        setFormData({
                                                            ...formData,
                                                            interval_config: { ...formData.interval_config, days_of_week: newDays }
                                                        });
                                                    }}
                                                    sx={{
                                                        fontWeight: 600,
                                                        borderRadius: '8px',
                                                        bgcolor: isSelected ? 'primary.main' : 'rgba(10, 42, 33, 0.05)',
                                                        color: isSelected ? '#fff' : 'primary.main',
                                                        '&:hover': {
                                                            bgcolor: isSelected ? 'primary.dark' : 'rgba(10, 42, 33, 0.1)',
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </FormField>
                            )}

                            {formData.interval === RecurringInterval.DAYS_OF_MONTH && (
                                <FormField label={t("Select Day(s) of Month")}>
                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(7, 1fr)', 
                                        gap: 1,
                                        p: 2,
                                        bgcolor: 'rgba(10, 42, 33, 0.03)',
                                        borderRadius: 2
                                    }}>
                                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                                            const isSelected = formData.interval_config?.days_of_month?.includes(day);
                                            return (
                                                <Button
                                                    key={day}
                                                    size="small"
                                                    variant={isSelected ? "contained" : "text"}
                                                    disableElevation
                                                    sx={{
                                                        minWidth: 0,
                                                        p: 0,
                                                        aspectRatio: '1/1',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 700,
                                                        borderRadius: 1.5,
                                                        bgcolor: isSelected ? 'primary.main' : 'transparent',
                                                        color: isSelected ? '#fff' : 'primary.main',
                                                        '&:hover': {
                                                            bgcolor: isSelected ? 'primary.dark' : 'rgba(10, 42, 33, 0.08)',
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        const currentDays = formData.interval_config?.days_of_month || [];
                                                        const newDays = isSelected
                                                            ? currentDays.filter(d => d !== day)
                                                            : [...currentDays, day].sort((a, b) => a - b);
                                                        setFormData({
                                                            ...formData,
                                                            interval_config: { ...formData.interval_config, days_of_month: newDays }
                                                        });
                                                    }}
                                                >
                                                    {day}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                </FormField>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                                <FormField label={t("Start Date")} id="start_date" required sx={{ flex: 1 }}>
                                    <TextField
                                        id="start_date"
                                        type="date"
                                        fullWidth
                                        variant="filled"
                                        InputProps={{ disableUnderline: true }}
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.start_date}
                                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                        sx={filledInputSx}
                                    />
                                </FormField>
                                <FormField label={t("End Date (Optional)")} id="end_date" sx={{ flex: 1 }}>
                                    <TextField
                                        id="end_date"
                                        type={formData.end_date ? 'date' : 'text'}
                                        onFocus={(e) => (e.target.type = 'date')}
                                        onBlur={(e) => (e.target.type = formData.end_date ? 'date' : 'text')}
                                        placeholder={t("No end date")}
                                        fullWidth
                                        variant="filled"
                                        InputProps={{ disableUnderline: true }}
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.end_date}
                                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                        sx={filledInputSx}
                                    />
                                </FormField>
                            </Box>
                        </Stack>
                    </FormSection>

                    <FormField>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    sx={{
                                        color: 'primary.main',
                                        '&.Mui-checked': { color: 'primary.main' }
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {t("Active Rule")}
                                </Typography>
                            }
                        />
                    </FormField>
                </Stack>
            </FormDialog>

            {/* Action Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                {userRole !== 'viewer' && (
                    <>
                        <MenuItem onClick={() => handleOpenEdit(transactions.find(e => e.id === selectedTxId))}>{t("Edit")}</MenuItem>
                        <MenuItem onClick={initiateDelete} sx={{ color: 'error.main' }}>{t("Delete")}</MenuItem>
                    </>
                )}
            </Menu>

            {/* Delete Confirmation Dialog */}
            <FormDialog
                open={deleteDialogOpen}
                onClose={isDeleting ? undefined : () => setDeleteDialogOpen(false)}
                onSubmit={confirmDelete}
                title={t("Delete Recurring Rule")}
                subtitle={t("Are you sure you want to delete this recurring rule? This action cannot be undone.")}
                saveLabel={t("Delete Rule")}
                saveDisabled={isDeleting}
                saving={isDeleting}
                maxWidth="xs"
            >
                <Box>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                        {t("Deleting this rule will stop future transactions from being generated.")}
                    </Typography>
                    
                    <Box sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        bgcolor: 'rgba(211, 47, 47, 0.05)',
                        border: '1px solid rgba(211, 47, 47, 0.1)'
                    }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={deleteLinkedEntries}
                                    onChange={(e) => setDeleteLinkedEntries(e.target.checked)}
                                    sx={{
                                        color: 'error.main',
                                        '&.Mui-checked': { color: 'error.main' }
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                    {t("Also delete all historical entries generated by this rule")}
                                </Typography>
                            }
                        />
                    </Box>
                </Box>
            </FormDialog>
        </Container>
    );
}
